class SearchDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get shouldAppendToBody() {
    return false;
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

class SearchTyped extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get startDelay() {
    return parseFloat(this.dataset.delay || 0);
  }

  async init() {
    this.insertCursor();
    await this.start(this.dataset.firstText, this.startDelay);

    setTimeout(async () => {
      await this.reset();
      await this.start(this.dataset.lastText, 0);
    }, 600);
  }

  async start(text, delay) {
    this.innerHTML = text;
    await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
    this.cursor.classList.add('blink');
  }

  async reset() {
    this.cursor.classList.remove('blink');
    await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
  }

  insertCursor() {
    if (this.cursor) return;

    this.cursor = document.createElement('span');
    this.cursor.className = 'typed-cursor';
    this.cursor.setAttribute('aria-hidden', true);
    this.cursor.innerHTML = '|';
    this.parentElement.insertBefore(this.cursor, this.nextSibling);
  }
}
customElements.define('search-typed', SearchTyped);

class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();

    this.cachedMap = new Map();
    this.focusElement = this.input;

    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('click', this.hideTypewriter.bind(this));
    this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get typewriter() {
    return this.input.previousElementSibling;
  }

  hideTypewriter() {
    if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
      this.typewriter.setAttribute('hidden', '');
    }
  }

  onFocus(event) {
    if (this.closest('.collection')) {
      document.body.classList.add('predictive-search-open');

      const searchTerm = this.getQuery();
      if (searchTerm.length === 0) return;

      const url = this.buildUrl().toString();
      this.renderSection(url, event);
    }
  }

  clear(event = null) {
    if (event) event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  getQuery() {
    return this.input.value.trim();
  }

  buildUrl() {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

    url.searchParams.set('q', this.getQuery());
    url.searchParams.set('resources[type]', 'product');
    // Retrieve Shopify's maximum predictive product candidates before ranking.
    url.searchParams.set('resources[limit]', 10);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', theme.utils.sectionId(this));
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }

    this.hideTypewriter();

    const url = this.buildUrl().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText);

    this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setAttribute('loading', '');

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);

        this.removeAttribute('loading');
        this.setAttribute('results', '');
      });
  }

  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
    if (document.getElementById(id) === null) return;

    const target = document.getElementById(id);
    const source = new DOMParser()
      .parseFromString(responseText, 'text/html')
      .getElementById(id);

    if (!target || !source) return;

    target.innerHTML = source.innerHTML;
    prioritizeSearchCards(target, this.getQuery(), Number(this.dataset.limit || 3));
  }
}
function normalizeSearchPriorityValue(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '');
}

function normalizeSearchPriorityText(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function getSearchPriority(card, query) {
  const searchText = normalizeSearchPriorityText(query);
  const searchCompact = normalizeSearchPriorityValue(query);
  const available = card.dataset.searchAvailable === 'true';

  if (!searchText) return available ? 20 : 21;

  const titleText = normalizeSearchPriorityText(card.dataset.searchTitle);
  const vendorText = normalizeSearchPriorityText(card.dataset.searchVendor);
  const titleCompact = normalizeSearchPriorityValue(card.dataset.searchTitle);
  const vendorCompact = normalizeSearchPriorityValue(card.dataset.searchVendor);
  const brandTitle = [vendorText, titleText].filter(Boolean).join(' ');

  const queryWords = searchText.split(/\s+/).filter(Boolean);
  const titleWords = titleText.split(/\s+/).filter(Boolean);
  const vendorWords = vendorText.split(/\s+/).filter(Boolean);
  const nameWords = new Set([...titleWords, ...vendorWords]);

  const directText = normalizeSearchPriorityText([
    card.dataset.searchTitle,
    card.dataset.searchType,
    card.dataset.searchTags,
    card.dataset.searchHandle,
    card.dataset.searchVendor
  ].filter(Boolean).join(' '));

  // Avoid putting a compatible accessory ahead of the exact device
  // when the shopper searches for a device model, not an accessory.
  const accessoryWords = new Set([
    'strap', 'straps', 'band', 'bands', 'case', 'cases',
    'protector', 'protectors', 'charger', 'chargers',
    'charging', 'cable', 'cables', 'battery', 'batteries',
    'cover', 'covers', 'sleeve', 'sleeves', 'adapter',
    'adapters', 'stand', 'stands', 'accessory',
    'accessories', 'screen', 'replacement'
  ]);
  const accessoryIntent = queryWords.some((word) => accessoryWords.has(word));
  const accessoryTitle = !accessoryIntent &&
    titleWords.some((word) => accessoryWords.has(word));
  const accessoryPenalty = accessoryTitle ? 6 : 0;

  // Strong title and brand+title matches outrank stock status.
  const exactName = titleText === searchText ||
    titleCompact === searchCompact ||
    brandTitle === searchText;
  const combinedPrefix = queryWords.length > 1 &&
    brandTitle.startsWith(searchText + ' ');
  const titlePrefix = titleText.startsWith(searchText + ' ');

  // Numeric/alphanumeric model tokens must be actual title tokens;
  // a compatible model buried in a strap's tags is not an exact match.
  const modelWord = queryWords.find(
    (word) => word.length >= 2 && /\d/.test(word)
  );
  const exactModelInTitle = Boolean(modelWord) &&
    queryWords.every((word) => nameWords.has(word)) &&
    titleWords.includes(modelWord);

  // Exact brand searches such as Epiphone and Seiko take priority.
  const exactVendor = vendorText === searchText ||
    vendorCompact === searchCompact;
  const nameTokenMatch = queryWords.every((word) => nameWords.has(word)) &&
    queryWords.some((word) => titleWords.includes(word));
  const vendorPrefix = searchCompact.length >= 2 &&
    vendorCompact.startsWith(searchCompact);
  const weakDirectMatch = queryWords.every(
    (word) => directText.includes(word)
  );

  if (exactName) return 0;
  if (combinedPrefix) return available ? 1 : 2;
  if (exactVendor) return available ? 3 : 4;
  if (titlePrefix) return (available ? 5 : 6) + accessoryPenalty;
  if (exactModelInTitle) return (available ? 7 : 8) + accessoryPenalty;
  if (nameTokenMatch) return (available ? 9 : 10) + accessoryPenalty;
  if (vendorPrefix) return available ? 15 : 16;
  if (weakDirectMatch) return available ? 17 : 18;
  return available ? 19 : 20;
}

function prioritizeSearchCards(root, query, visibleLimit = null) {
  const groups = [];

  if (root.matches?.('.horizontal-products, .card-grid')) {
    groups.push(root);
  }

  root.querySelectorAll?.('.horizontal-products, .card-grid').forEach((group) => groups.push(group));

  groups.forEach((group) => {
    const cards = Array.from(group.children).filter((card) => card.dataset?.searchVendor !== undefined);
    if (cards.length === 0) return;

    // Keep non-product search results/promotions after products on the full results grid.
    if (group.classList.contains('card-grid')) {
      Array.from(group.children).forEach((child) => {
        if (child.dataset?.searchVendor === undefined) child.style.order = '99999';
      });
    }

    const ranked = cards
      .map((card, index) => ({ card, index, rank: getSearchPriority(card, query) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index);

    ranked.forEach(({ card, rank }, index) => {
      card.style.order = String(rank);
      card.hidden = visibleLimit !== null && index >= visibleLimit;
    });
  });
}

function prioritizeSearchPageResults() {
    if (!document.body.classList.contains('template-search')) return;
  const grid = document.querySelector('#ProductGridContainer .card-grid');
  if (!grid) return;

  const query = new URLSearchParams(window.location.search).get('q') || '';
  prioritizeSearchCards(grid, query);
}

function initSearchPagePriority() {
  prioritizeSearchPageResults();

  const container = document.getElementById('ProductGridContainer');
  if (!container || container.dataset.vendorPriorityObserver === 'true') return;

  container.dataset.vendorPriorityObserver = 'true';
  new MutationObserver(() => prioritizeSearchPageResults()).observe(container, {
    childList: true,
    subtree: true
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSearchPagePriority);
} else {
  initSearchPagePriority();
}

customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

class PredictiveSearchOverlay extends OverlayElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  onClick() {
    setTimeout(() => {
      document.body.classList.remove('predictive-search-open');
    });
  }
}
customElements.define('predictive-search-overlay', PredictiveSearchOverlay);
